import React from "react";
import { Badge } from "src/components/ui/Badge";
import { Button } from "src/components/ui/Button";
import {
  Clock,
  Calendar as CalendarIcon,
  Video,
  Edit2,
  Star,
  X,
  BookOpen,
} from "lucide-react";
import { Link } from "react-router-dom";
import { getSessionStatus } from "src/utils/sessionUtils";

// Helper function to format session time
const formatSessionTime = (scheduledAt: string) => {
  const date = new Date(scheduledAt);
  return {
    date: date.toLocaleDateString(),
    time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  };
};

// Helper function to get tutor name
const getTutorName = (tutorProfile: any) => {
  if (!tutorProfile) return 'Unknown Tutor';
  return `${tutorProfile.first_name || ''} ${tutorProfile.last_name || ''}`.trim() || 'Unknown Tutor';
};

// Helper function to get tutor display name with credentials
const getTutorDisplayName = (tutorProfile: any, tutorData: any) => {
  const baseName = getTutorName(tutorProfile);
  if (baseName === 'Unknown Tutor') return baseName;

  // Add education level if available from new education structure
  if (tutorData?.education && Array.isArray(tutorData.education) && tutorData.education.length > 0) {
    // Find the highest degree
    const hasPhd = tutorData.education.some((edu: any) => edu.degree?.toLowerCase().includes('phd') || edu.degree?.toLowerCase().includes('doctorate'));
    const hasMaster = tutorData.education.some((edu: any) => edu.degree?.toLowerCase().includes('master') || edu.degree?.toLowerCase().includes('m.s.') || edu.degree?.toLowerCase().includes('m.a.'));
    
    if (hasPhd) {
      return `Dr. ${baseName}`;
    } else if (hasMaster) {
      return `Prof. ${baseName}`;
    }
  }

  return baseName;
};

// Compatibility helpers used across existing session views. Their inputs are now
// canonical curriculum records: unit, objective, and domain/subject.
const getTopicName = (unit: any) => {
  return unit?.name || 'Unassigned unit';
};

// Helper function to get the learning objective title
const getSubtopicName = (objective: any) => {
  return objective?.title || null;
};

// Helper function to get tutor photo
const getTutorPhoto = (tutorProfile: any) => {
  return tutorProfile?.profile_picture_url ||
    "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80";
};

// Helper function to get tutor rating
const getTutorRating = (tutorData: any) => {
  return tutorData?.rating || null;
};

// Helper function to generate human-readable session ID
const generateSessionId = (sessionUuid: string) => {
  // Create a simple hash from UUID to generate consistent session numbers
  let hash = 0;
  for (let i = 0; i < sessionUuid.length; i++) {
    const char = sessionUuid.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  // Ensure positive number and add base offset
  const sessionNumber = Math.abs(hash % 9000) + 1000;
  return `S-${sessionNumber}`;
};

// Helper function to get the subject name from a curriculum unit
const getSubjectName = (unit: any) => {
  return unit?.domain?.subject?.name || unit?.name || 'Unassigned subject';
};


interface SessionCardProps {
  session: any;
  showActions?: boolean;
  showSessionId?: boolean;
  onReschedule?: (session: any) => void;
  onJoin?: (session: any) => void;
  onCancel?: (session: any) => void;
}

const SessionCard: React.FC<SessionCardProps> = ({
  session,
  showActions = true,
  showSessionId = false,
  onReschedule,
  onJoin,
  onCancel
}) => {
  const { date, time } = formatSessionTime(session.scheduled_at);
  const tutorDisplayName = getTutorDisplayName(session.tutor_profile, session.tutor_data);
  const tutorPhoto = getTutorPhoto(session.tutor_profile);
  const topicName = getTopicName(session.curriculum_unit);
  const subtopicName = getSubtopicName(session.curriculum_objective);
  const tutorRating = getTutorRating(session.tutor_data);
  const sessionId = generateSessionId(session.id);
  const sessionStatus = getSessionStatus(session);

  const handleReschedule = () => {
    if (onReschedule) {
      onReschedule(session);
    }
  };

  const handleJoin = () => {
    if (onJoin) {
      onJoin(session);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel(session);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200">
      {showSessionId && (
        <div className="px-6 pt-4">
          <Badge variant="outline" className="text-xs">
            {sessionId}
          </Badge>
        </div>
      )}
      
      <div className="p-6">
        {/* Header Section - Tutor Info */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <img
              src={tutorPhoto}
              alt={tutorDisplayName}
              className="w-12 h-12 rounded-full object-cover ring-2 ring-gray-100"
            />
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900">{tutorDisplayName}</h3>
                {tutorRating && (
                  <div className="flex items-center bg-yellow-50 px-2 py-1 rounded-full">
                    <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                    <span className="text-xs text-yellow-700 ml-1 font-medium">{tutorRating}</span>
                  </div>
                )}
              </div>
              {session.tutor_data?.subjects_taught && (
                <p className="text-xs text-gray-500 mt-1">
                  Specializes in: {(() => {
                    // Handle both array and string formats
                    let subjects: string[] = [];
                    if (Array.isArray(session.tutor_data.subjects_taught)) {
                      subjects = session.tutor_data.subjects_taught;
                    } else if (typeof session.tutor_data.subjects_taught === 'string') {
                      // Try to parse if it's a string representation of an array
                      try {
                        const parsed = JSON.parse(session.tutor_data.subjects_taught);
                        subjects = Array.isArray(parsed) ? parsed : [session.tutor_data.subjects_taught];
                      } catch {
                        subjects = [session.tutor_data.subjects_taught];
                      }
                    }
                    
                    if (subjects.length === 0) return 'No specializations listed';
                    
                    const displaySubjects = subjects.slice(0, 3);
                    const remainingCount = subjects.length - 3;
                    
                    return displaySubjects.join(', ') + 
                           (remainingCount > 0 ? ` and ${remainingCount} more` : '');
                  })()}
                </p>
              )}
            </div>
          </div>
          
          <Badge
            variant={sessionStatus.variant}
            className={`capitalize ${sessionStatus.status === 'live' ? 'animate-pulse bg-red-100 text-red-800 border-red-200' : ''}`}
          >
            {sessionStatus.label}
          </Badge>
        </div>

        {/* Session Content Section */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {/* Session curriculum unit and learning objective */}
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="h-4 w-4 text-blue-600" />
                <div>
                  {subtopicName ? (
                    <div>
                      <h4 className="font-semibold text-gray-900 text-lg">{subtopicName}</h4>
                      <p className="text-sm text-gray-600">{topicName}</p>
                    </div>
                  ) : (
                    <h4 className="font-semibold text-gray-900 text-lg">{topicName}</h4>
                  )}
                </div>
              </div>
              
              {/* Batch Information */}
              {session.batch?.name && (
                <div className="mt-2">
                  <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">
                    {session.batch.name}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Session Details */}
        <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <CalendarIcon className="h-4 w-4" />
              <span>{date}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>{time}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="font-medium">{session.duration_min} min</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        {showActions && (
          <div className="flex justify-end space-x-2">
            {onReschedule && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleReschedule}
                className="text-gray-600 hover:text-gray-800"
              >
                <Edit2 className="h-4 w-4 mr-1" />
                Reschedule
              </Button>
            )}
            {onCancel && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
            )}
            {onJoin && sessionStatus.canJoin && (
              <Button
                size="sm"
                onClick={handleJoin}
                className={`${sessionStatus.status === 'live' ? 'bg-red-600 hover:bg-red-700 text-white animate-pulse' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
              >
                <Video className="h-4 w-4 mr-1" />
                {sessionStatus.status === 'live' ? 'Join Now' : 'Join'}
              </Button>
            )}
            {/* Fallback to Link-based actions if no handlers provided */}
            {!onReschedule && !onJoin && !onCancel && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  asChild
                  className="text-gray-600 hover:text-gray-800"
                >
                  <Link to={`/edit-session/${session.id}`}>
                    <Edit2 className="h-4 w-4 mr-1" />
                    Reschedule
                  </Link>
                </Button>
                <Button
                  size="sm"
                  asChild
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Link to={`/join-session/${session.id}`}>
                    <Video className="h-4 w-4 mr-1" />
                    Join
                  </Link>
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionCard;
export { formatSessionTime, getTutorDisplayName, getTopicName, getSubtopicName, getTutorPhoto, getTutorRating, generateSessionId, getSubjectName };
